import { ODOO_BASE_URL } from '../../odooConfig';

const isLocalDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// On localhost, use relative URLs so setupProxy can forward to config._odooBaseUrlProduction.
const BASE_URL = isLocalDevelopment ? '' : ODOO_BASE_URL;

console.log(
  'Employee Attendance API:',
  BASE_URL || '(proxy → config._odooBaseUrlProduction — restart npm start after config changes)'
);

const API_PREFIX = '/api/employee-attendance';

/**
 * Employee Attendance Portal API client (JSON-RPC 2.0 + Bearer token)
 */
class EmployeeAttendanceAPI {
  constructor(baseURL = BASE_URL) {
    this.baseURL = baseURL;
    this.onUnauthorized = null;
  }

  setUnauthorizedHandler(handler) {
    this.onUnauthorized = handler;
  }

  async request(endpoint, params = {}, token = null, options = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        jsonrpc: '2.0',
        method: 'call',
        params,
        id: Date.now(),
      }),
    });

    if (response.status === 401 && this.onUnauthorized && !options.suppressUnauthorized) {
      this.onUnauthorized();
    }

    if (!response.ok) {
      const errorText = await response.text();
      if (response.status === 401 && this.onUnauthorized && !options.suppressUnauthorized) {
        this.onUnauthorized();
      }
      throw new Error(errorText || response.statusText);
    }

    const data = await response.json();

    if (data.error) {
      const message = data.error.message || data.error.data?.message || 'API request failed';
      const err = new Error(message);
      err.errorData = data.error;
      throw err;
    }

    const result = data.result ?? data;

    if (result?.success === false) {
      const code = result.code ?? 400;
      if (code === 401 || result.error === 'Unauthorized') {
        if (this.onUnauthorized && !options.suppressUnauthorized) this.onUnauthorized();
      }
      const message =
        result.message || result.error || data.error?.message || 'API request failed';
      const err = new Error(message);
      err.code = code;
      err.result = result;
      throw err;
    }

    return result;
  }

  /**
   * Plain JSON request (no JSON-RPC 2.0 wrapping) — used for /info and /change-password
   */
  async plainRequest(endpoint, params = {}, token = null, options = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    if (response.status === 401 && this.onUnauthorized && !options.suppressUnauthorized) {
      this.onUnauthorized();
    }

    // Try to parse the response body as JSON regardless of status
    let data;
    const responseText = await response.text();
    try {
      data = JSON.parse(responseText);
    } catch {
      // Not JSON — could be HTML error page
      if (!response.ok) {
        const isHTML = responseText.trim().startsWith('<!') || responseText.trim().startsWith('<html');
        if (isHTML) {
          throw new Error(
            'Something went wrong. The server could not process your request. Please try again later.'
          );
        }
        throw new Error(responseText || 'Something went wrong. Please try again.');
      }
      throw new Error('Unexpected response from server. Please try again.');
    }

    // Handle error responses (both non-OK status and success: false in body)
    if (!response.ok || data?.success === false) {
      const code = data?.code ?? response.status ?? 400;
      if (code === 401 || data?.error === 'Unauthorized') {
        if (this.onUnauthorized && !options.suppressUnauthorized) this.onUnauthorized();
      }
      // Extract the user-friendly message from the parsed JSON
      const message = this._friendlyMessage(data?.message, code) ||
                      data?.message ||
                      data?.error ||
                      'Something went wrong. Please try again.';
      const err = new Error(message);
      err.code = code;
      err.result = data;
      throw err;
    }

    return data;
  }

  /**
   * Maps known backend messages to friendlier versions for the user.
   * Returns null if no mapping found (falls back to the original message).
   */
  _friendlyMessage(message, code) {
    if (!message) return null;
    const lower = message.toLowerCase();

    // Password-specific
    if (lower.includes('at least 8 characters'))
      return 'Your new password must be at least 8 characters long.';
    if (lower.includes('current password is incorrect'))
      return 'The current password you entered is incorrect. Please try again.';
    if (lower.includes('passwords must match'))
      return 'The new passwords you entered don\'t match. Please re-enter them.';

    // Auth
    if (lower.includes('session expired') || lower.includes('token'))
      return 'Your session has expired. Please log in again.';
    if (code === 401)
      return 'You are not authorized. Please log in again.';

    // Server
    if (code === 500)
      return 'Something went wrong on the server. Please try again later or contact support.';
    if (code === 404)
      return 'The requested feature is not available. Please contact support.';

    return null; // No mapping — use original message
  }

  ping() {
    return this.request(`${API_PREFIX}/ping`);
  }

  login(username, password) {
    return this.request(`${API_PREFIX}/login`, { username, password });
  }

  logout(token) {
    return this.request(`${API_PREFIX}/logout`, {}, token);
  }

  me(token, options = {}) {
    return this.request(`${API_PREFIX}/me`, {}, token, options);
  }

  getToday(token) {
    return this.request(`${API_PREFIX}/today`, {}, token);
  }

  getStatus(token) {
    return this.request(`${API_PREFIX}/status`, {}, token);
  }

  checkIn(token) {
    return this.request(`${API_PREFIX}/check-in`, {}, token);
  }

  checkOut(token) {
    return this.request(`${API_PREFIX}/check-out`, {}, token);
  }

  getRecord(params, token) {
    return this.request(`${API_PREFIX}/record`, params, token);
  }

  getHistory(params, token) {
    return this.request(`${API_PREFIX}/history`, params, token);
  }

  getReportSummary(params, token) {
    return this.request(`${API_PREFIX}/report/summary`, params, token);
  }

  getReportDetail(params, token) {
    return this.request(`${API_PREFIX}/report/detail`, params, token);
  }

  getInfo(token) {
    return this.plainRequest(`${API_PREFIX}/info`, {}, token);
  }

  changePassword(currentPassword, newPassword, token) {
    return this.plainRequest(
      `${API_PREFIX}/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      token
    );
  }
}

const employeeAttendanceAPI = new EmployeeAttendanceAPI();
export default employeeAttendanceAPI;
